import {
  Component,
  OnInit,
  ViewChild,
  AfterViewInit,
  Input,
  OnChanges,
  ContentChildren,
  QueryList,
  input,
  signal,
  TemplateRef,
  output,
} from "@angular/core";
import {
  DatatableComponent as NgxDatatableComponent,
  ColumnMode,
  SelectionType,
  NgxDatatableModule,
} from "@swimlane/ngx-datatable";
import { DataTableColumnDirective as NgxDataTableColumnDirective } from "@swimlane/ngx-datatable";
import { FormsModule } from "@angular/forms";
import { NgTemplateOutlet } from "@angular/common";

@Component({
  selector: "portal-datatable",
  templateUrl: "./datatable.component.html",
  styleUrls: ["./datatable.component.scss"],
  standalone: true,
  imports: [NgxDatatableModule, FormsModule, NgTemplateOutlet],
})
export class DatatableComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild(NgxDatatableComponent) table!: NgxDatatableComponent;
  @ContentChildren(NgxDataTableColumnDirective) columns!: QueryList<NgxDataTableColumnDirective>;

  /**
   * This attribute contain the data
   */
  readonly rows = input<object[] | undefined>();

  /**
   * This attribute contain the selected data
   */
  readonly selected = input<object[]>([]);

  /**
   * This attribute set the page size
   */
  readonly limit = input<number>(10);

  /**
   * This attribute set the option for the page size
   */
  readonly numberOfItemOption = signal([10, 25, 50, 100]);

  /**
   * This attribute set the text on the right side of the option dropdown
   */
  readonly optionText = input<string>("Zeilen anzeigen");

  /**
   * Type of column width distribution formula.
   * Example: flex, force, standard
   * Force is default for full width
   */
  readonly columnMode = input<ColumnMode>(ColumnMode.force);

  /**
   * Type of row selection. Options are:
   *
   *  - `single`
   *  - `multi`
   *  - `checkbox`
   *  - `multiClick`
   *  - `cell`
   *
   * For no selection pass a `falsey`.
   * Default value: `undefined`
   */
  readonly selectionType = input<SelectionType>(SelectionType.multiClick);

  /**
   * If the table should use external paging
   * otherwise its assumed that all data is preloaded.
   */
  readonly externalPaging = input<boolean>(false);

  /**
   * If the table should use external sorting or
   * the built-in basic sorting.
   */
  readonly externalSorting = input<boolean>(false);

  /**
   * If the table should use external sorting/paging
   * count should be set
   */
  readonly count = input<number>(0);

  /**
   * If the table should use external sorting/paging
   * offset should be set
   */
  readonly offset = input<number>(0);

  /**
   * Show the linear loading bar.
   * Default value: `false`
   */
  readonly loadingIndicator = input<boolean>(false);

  /**
   * If the table header should be visible.
   */
  readonly datatableInfoTextVisible = input<boolean>(true);

  /**
   * If the table footer should be visible.
   */
  readonly datatableFooterVisible = input<boolean>(true);

  /**
   * Row specific classes.
   * Similar implementation to ngClass.
   *
   *  [rowClass]="'first second'"
   *  [rowClass]="{ 'first': true, 'second': true, 'third': false }"
   */
  @Input() rowClass: ((row: object) => Record<string, boolean>) | undefined;

  /**
   * Array of sorted columns by property and type.
   * Default value: `[]`
   */

  readonly sorts = input<Record<string, string>[] | undefined>();

  /**
   * Template for row detail
   */
  readonly rowDetailTemplate = input<TemplateRef<Element> | undefined>();

  /**
   * Height for row detail
   */
  readonly rowDetailHeight = input<number>(0);

  /**
   * Column activation was invoked.
   */
  readonly activate = output<unknown>();

  /**
   * Column sort was invoked.
   */
  readonly sort = output<unknown>();

  /**
   * The table pagination triggered
   */
  readonly page = output<unknown>();

  /**
   * The tables page size was selected/changed
   */
  readonly rowsLimit = output<unknown>();

  /**
   * The row selection was triggered
   */
  readonly select = output<unknown>();

  datatableInfoText: string | undefined;
  readonly currentLimit = signal<number>(10);
  readonly currentOffset = signal<number>(0);
  readonly currentSelected = signal<object[]>([]);

  constructor() {
    // workaround deselect single row
    // https://github.com/swimlane/ngx-datatable/issues/775
    this.singleSelectCheck = this.singleSelectCheck.bind(this);
  }

  ngOnInit() {
    // workaround of ng-content
    // https://github.com/swimlane/ngx-datatable/issues/1299
    setTimeout(() => {
      this.table.columnTemplates = this.columns;
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (this.rows()) {
        this.#updateDataTableInfo();
      }
    });
  }

  ngOnChanges() {
    setTimeout(() => {
      if (this.rows()?.length) {
        this.#updateDataTableInfo();
      }
      // add limit if not exist in default option and sort option
      if (!this.numberOfItemOption()?.includes(this.currentLimit())) {
        this.numberOfItemOption.set([...this.numberOfItemOption(), this.currentLimit()].sort((a, b) => a - b));
      }
    });
  }

  // workaround deselect single row
  // https://github.com/swimlane/ngx-datatable/issues/775
  singleSelectCheck(row: object) {
    if (this.selectionType() === "single") {
      return this.currentSelected().indexOf(row) === -1;
    }
    return this.currentSelected();
  }

  //#region output event
  onActivate(event: unknown) {
    this.activate.emit(event);
  }

  // selection implemented in datatable
  onSelect({ selected }: { selected: object[] }) {
    this.currentSelected.set(selected);
    this.select.emit({ selected });
  }

  onPaginated(event: unknown) {
    this.table.limit = this.currentLimit();
    this.table.recalculate();
    this.page.emit(event);
  }

  onSort(event: unknown) {
    this.sort.emit(event);
  }

  onChangeRowsLimit(limit: string) {
    this.currentLimit.set(parseInt(limit, 10));
    this.table.limit = this.currentLimit();
    this.table.recalculate();
    this.rowsLimit.emit(this.currentLimit());
    this.#updateDataTableInfo();
  }

  pageChanged() {
    this.currentOffset.set(this.table.offset);
    this.#updateDataTableInfo();
  }

  #updateDataTableInfo() {
    const totalRow = this.table.bodyComponent.rowCount;
    let startRowOfCurrentPage;
    let endRowOfCurrentPage;

    if (!this.#isOnePage()) {
      startRowOfCurrentPage = 1 + this.currentOffset() * this.currentLimit();
    } else {
      startRowOfCurrentPage = 1;
    }

    if (!this.#isLastPage()) {
      endRowOfCurrentPage = this.currentLimit() + this.currentOffset() * this.currentLimit();
    } else {
      endRowOfCurrentPage = totalRow;
    }

    if (totalRow === 0) {
      startRowOfCurrentPage = 0;
    }
    this.datatableInfoText = startRowOfCurrentPage + " bis " + endRowOfCurrentPage + " von " + totalRow + " Einträgen";
  }

  #isOnePage(): boolean {
    const totalRow = this.table.bodyComponent.rowCount;
    return this.currentLimit() >= totalRow;
  }

  #isLastPage(): boolean {
    const totalRow = this.table.bodyComponent.rowCount;
    const maxRows = (this.currentOffset() + 1) * this.currentLimit();
    return maxRows >= totalRow;
  }
}
